/**
 * Detect quests that have become physically impossible to finish because
 * the underlying pool is exhausted, and auto-complete them so the day
 * can still turn green.
 *
 * The canonical case driving this code path is the `new_words` family:
 * the event only fires on a first-sighting item the child gets right.
 * Once they've exposed (correctly OR incorrectly) every item in their
 * grade, no future session can ever fire another `new_words` event —
 * a stuck quest would permanently block the day's completion.
 *
 * The same logic could be extended to other event types later (e.g. a
 * `mistakes_cleared` quest could end up stuck if the wrong pool empties
 * faster than the quest target). For now we only handle `new_words`
 * because that's the type that actually has a known finite pool the
 * helper can count against.
 */
import {
  GRADES,
  getAllItemsForGrade,
  getSessionPoolForGrade,
} from '../data/index.js';
import { getUnseenItems } from './adaptive.js';

/**
 * Count items the child has never attempted that they could actually
 * encounter at this grade. Uses the SESSION pool (cumulative words +
 * grade-locked sentences) so the quest-clamp heuristic stays in sync
 * with what `buildSessionForGrade` will actually serve: a Γ΄ kid who
 * exhausted Γ΄ unseen but still has Β΄ revision available is NOT
 * exhausted.
 */
export function countUnseenInGrade(grade, itemTracking) {
  if (!itemTracking || typeof itemTracking !== 'object') return 0;
  if (grade == null) return 0;
  const items = getSessionPoolForGrade(grade);
  return getUnseenItems(items, itemTracking).length;
}

/**
 * Cross-grade count — kept around for callers that genuinely want the
 * global pool (e.g. a future "spread across grades" tutor mode). Uses
 * `getAllItemsForGrade` so each item is counted exactly once, even
 * though `getSessionPoolForGrade` would double-count lower-grade words.
 */
export function countUnseenAcrossGrades(itemTracking) {
  if (!itemTracking || typeof itemTracking !== 'object') return 0;
  let count = 0;
  for (const g of GRADES) {
    const items = getAllItemsForGrade(g);
    count += getUnseenItems(items, itemTracking).length;
  }
  return count;
}

/**
 * Walk a quest slice and auto-complete any quest whose remaining target
 * exceeds what the user could possibly still attempt. Returns the same
 * slice reference when nothing changed (avoids spurious re-renders).
 */
export function clampImpossibleQuests(slice, unseenAvailable) {
  if (!slice || !Array.isArray(slice.quests)) return slice;
  let changed = false;
  const quests = slice.quests.map((q) => {
    if (q.completed) return q;
    if (q.type === 'new_words') {
      const target = Number.isFinite(q.target) ? q.target : 0;
      const progress = Number.isFinite(q.progress) ? q.progress : 0;
      const remaining = target - progress;
      if (remaining > unseenAvailable) {
        changed = true;
        return { ...q, progress: target, completed: true };
      }
    }
    return q;
  });
  return changed ? { ...slice, quests } : slice;
}
